import { DndContext, closestCenter, DragEndEvent, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, arrayMove, rectSortingStrategy } from '@dnd-kit/sortable';
import { UserWidget } from '@/config/dashboardWidgets';
import { WidgetContainer } from './WidgetContainer';
import { WidgetRenderer } from './WidgetRenderer';

interface DashboardGridProps {
  widgets: UserWidget[];
  onReorder: (widgets: UserWidget[]) => void;
  onRemove: (widgetId: string) => void;
  onResize: (widgetId: string, size: 'full' | 'half' | 'third') => void;
}

export function DashboardGrid({ widgets, onReorder, onRemove, onResize }: DashboardGridProps) {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    
    if (over && active.id !== over.id) {
      const oldIndex = widgets.findIndex(w => w.id === active.id);
      const newIndex = widgets.findIndex(w => w.id === over.id);
      onReorder(arrayMove(widgets, oldIndex, newIndex));
    }
  };

  if (widgets.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center border-2 border-dashed border-muted rounded-lg">
        <p className="text-muted-foreground mb-2">Nenhum indicador adicionado</p>
        <p className="text-sm text-muted-foreground">
          Clique em "Adicionar Indicador" para personalizar seu dashboard
        </p>
      </div>
    );
  }

  return (
    <DndContext 
      sensors={sensors}
      collisionDetection={closestCenter} 
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={widgets.map(w => w.id)} strategy={rectSortingStrategy}>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {widgets.map(widget => (
            <WidgetContainer
              key={widget.id}
              widget={widget}
              onRemove={onRemove}
              onResize={onResize}
            >
              <WidgetRenderer type={widget.type} />
            </WidgetContainer>
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
}
